import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Slide, Fade } from "@mui/material";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Stack from "react-bootstrap/Stack";
import { FaGithub, FaGitlab, FaBitbucket, FaMicrosoft } from "react-icons/fa";

import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import GeneralButton from "../../../common/SaveButton/GeneralButton";
import Images from "../../../../utils/images";
import { changeTitle } from "../../../../state/slices/header";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { showError, showSuccess } from "../../../../utils/showMessage";
import { createGitRepo, updateGitRepo, getScanConfigs } from "../../../../api/credentialScanning";
import "../index.scss";

const PROVIDERS = [
  { value: "github",       label: "GitHub",       icon: <FaGithub  size={16} />, color: "#24292e" },
  { value: "gitlab",       label: "GitLab",       icon: <FaGitlab  size={16} />, color: "#fc6d26" },
  { value: "bitbucket",    label: "Bitbucket",    icon: <FaBitbucket size={16} />, color: "#0052cc" },
  { value: "azure_devops", label: "Azure DevOps", icon: <FaMicrosoft size={16} />, color: "#0078d4" },
];

const PROVIDER_HINT_KEYS = {
  github:       "credentialScanning.gitHintGithub",
  gitlab:       "credentialScanning.gitHintGitlab",
  bitbucket:    "credentialScanning.gitHintBitbucket",
  azure_devops: "credentialScanning.gitHintAzureDevops",
};

const TagPill = ({ items, onRemove }) => {
  if (!items.length) return null;
  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {items.map((item, i) => (
        <span
          key={i}
          style={{
            background: "#e8eeff", color: "#5177ff",
            padding: "2px 10px", borderRadius: 20,
            fontSize: 12, display: "flex", alignItems: "center", gap: 5,
          }}
        >
          {item}
          <button
            type="button"
            onClick={() => onRemove(i)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#5177ff", padding: 0, lineHeight: 1, fontSize: 14 }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};

function GitConnectionFormPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const repo = location.state?.repo || null;
  const preselectedScanConfigId = location.state?.scanConfigId || null;
  const isEdit = !!repo;

  const [slideChecked, setSlideChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [scanConfigs, setScanConfigs] = useState([]);
  const [repoFilterInput, setRepoFilterInput] = useState("");
  const [branchFilterInput, setBranchFilterInput] = useState("");

  const [form, setForm] = useState({
    name: "",
    provider: "github",
    base_url: "",
    organization: "",
    repository_filter: [],
    branch_filter: [],
    scan_commit_history: false,
    commit_history_depth: 50,
    token: "",
    scan_config: preselectedScanConfigId || "",
  });

  useEffect(() => {
    dispatch(changeTitle("Discovery"));
    document.title = isEdit ? t("credentialScanning.editGitConnection") : t("credentialScanning.addGitConnection");
    setSlideChecked(true);

    if (!preselectedScanConfigId) {
      getScanConfigs().then(({ data }) => {
        if (data) setScanConfigs(Array.isArray(data) ? data : []);
      });
    }

    if (repo) {
      setForm({
        name: repo.name || "",
        provider: repo.provider || "github",
        base_url: repo.base_url || "",
        organization: repo.organization || "",
        repository_filter: repo.repository_filter || [],
        branch_filter: repo.branch_filter || [],
        scan_commit_history: repo.scan_commit_history ?? false,
        commit_history_depth: repo.commit_history_depth ?? 50,
        token: "",
        scan_config: repo.scan_config || preselectedScanConfigId || "",
      });
    }
  }, [repo, preselectedScanConfigId, isEdit, dispatch, t]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const addRepoFilter = () => {
    const v = repoFilterInput.trim();
    if (!v) return;
    set("repository_filter", [...form.repository_filter, v]);
    setRepoFilterInput("");
  };

  const addBranchFilter = () => {
    const v = branchFilterInput.trim();
    if (!v) return;
    set("branch_filter", [...form.branch_filter, v]);
    setBranchFilterInput("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const payload = { ...form };
    if (isEdit) payload.id = repo.id;
    if (!payload.token) delete payload.token;
    if (!payload.scan_config) payload.scan_config = null;

    const fn = isEdit ? updateGitRepo : createGitRepo;
    const { error: err } = await fn(payload);
    setSaving(false);

    if (err) {
      if (typeof err === "object" && !err.detail) {
        setErrors(err);
      } else {
        showError(err?.detail || t("credentialScanning.toastFailedToSaveGitConn"));
      }
    } else {
      showSuccess(isEdit ? t("credentialScanning.toastGitConnUpdated") : t("credentialScanning.toastGitConnAdded"));
      navigate("/dashboard/discovery/hardcoded-credentials/git-repos");
    }
  };

  return (
    <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
      <Fade timeout={fadedelayTime} in={slideChecked}>
        <div className="main_content_container p-3 w-100">
          <div className="position-relative bg-white p-4" style={{ minHeight: "calc(100vh - 120px)" }}>

            {/* Breadcrumb */}
            <div className="py-2 pt-2">
              <BreadcrumbHeading
                dataTableHeadingText={[
                  t("credentialScanning.breadcrumbBackTo"),
                  t("credentialScanning.gitConnections"),
                  isEdit ? t("credentialScanning.gitEditConnection") : t("credentialScanning.gitAddConnection"),
                ]}
                dataTablePageLink={[
                  "/dashboard/discovery/hardcoded-credentials",
                  "/dashboard/discovery/hardcoded-credentials/git-repos",
                  isEdit
                    ? `/dashboard/discovery/hardcoded-credentials/git-repos/${repo?.id}/edit`
                    : "/dashboard/discovery/hardcoded-credentials/git-repos/new",
                ]}
                pageNumber={5}
              />
            </div>

            <div className="mb-4">
              <ListHeading
                dataTableHeadingText={isEdit ? t("credentialScanning.editGitConnection") : t("credentialScanning.addGitConnection")}
                dataTableHeadingIcon={Images.NetworkAnalyzer}
              />
            </div>

            <Form onSubmit={handleSubmit}>
              <Container fluid className="px-0">
                <Row>
                  {/* Left column */}
                  <Col xs={12} md={6} className="pe-md-4">

                    {!preselectedScanConfigId && (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">{t("credentialScanning.gitFieldScanConfiguration")}</Form.Label>
                        <Form.Select
                          value={form.scan_config}
                          onChange={(e) => set("scan_config", e.target.value)}
                          isInvalid={!!errors.scan_config}
                        >
                          <option value="">{t("credentialScanning.gitNoneCanLinkLater")}</option>
                          {scanConfigs.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </Form.Select>
                        {errors.scan_config && <Form.Control.Feedback type="invalid">{errors.scan_config}</Form.Control.Feedback>}
                        <Form.Text className="text-muted">{t("credentialScanning.gitOptionalLinkLater")}</Form.Text>
                      </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.gitFieldConnectionName")} <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        required
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder={t("credentialScanning.gitPlaceholderConnectionName")}
                        isInvalid={!!errors.name}
                      />
                      {errors.name && <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>}
                    </Form.Group>

                    {/* Provider selector */}
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.gitFieldProvider")} <span className="text-danger">*</span></Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        {PROVIDERS.map((p) => {
                          const selected = form.provider === p.value;
                          return (
                            <button
                              key={p.value}
                              type="button"
                              onClick={() => set("provider", p.value)}
                              className={`cred-scan-provider-btn${selected ? " selected" : ""}`}
                              data-provider={p.value}
                            >
                              {p.icon}
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        {isEdit ? t("credentialScanning.gitAccessTokenEditLabel") : t("credentialScanning.gitAccessToken")}
                        {!isEdit && <span className="text-danger"> *</span>}
                      </Form.Label>
                      <Form.Control
                        type="password"
                        required={!isEdit}
                        value={form.token}
                        onChange={(e) => set("token", e.target.value)}
                        placeholder={PROVIDER_HINT_KEYS[form.provider] ? t(PROVIDER_HINT_KEYS[form.provider]) : t("credentialScanning.gitAccessTokenPlaceholder")}
                        autoComplete="new-password"
                        isInvalid={!!errors.token}
                      />
                      {errors.token && <Form.Control.Feedback type="invalid">{errors.token}</Form.Control.Feedback>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.gitFieldOrgWorkspace")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.organization}
                        onChange={(e) => set("organization", e.target.value)}
                        placeholder={t("credentialScanning.gitPlaceholderOrgWorkspace")}
                      />
                    </Form.Group>

                    {(form.provider === "gitlab" || form.provider === "bitbucket") && (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">{t("credentialScanning.gitFieldBaseUrl")} <span className="text-muted fw-normal">{t("credentialScanning.gitForSelfHosted")}</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={form.base_url}
                          onChange={(e) => set("base_url", e.target.value)}
                          placeholder={
                            form.provider === "gitlab"
                              ? "https://gitlab.example.com"
                              : "https://api.bitbucket.example.com/2.0"
                          }
                        />
                      </Form.Group>
                    )}

                  </Col>

                  {/* Right column */}
                  <Col xs={12} md={6}>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        {t("credentialScanning.gitFieldRepoFilter")} <span className="text-muted fw-normal">{t("credentialScanning.gitEmptyScanAll")}</span>
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          value={repoFilterInput}
                          onChange={(e) => setRepoFilterInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRepoFilter(); } }}
                          placeholder={t("credentialScanning.gitPlaceholderRepoName")}
                        />
                        <button
                          type="button"
                          onClick={addRepoFilter}
                          style={{
                            padding: "7px 16px", background: "#5177ff", color: "#fff",
                            border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap",
                          }}
                        >
                          {t("credentialScanning.gitBtnAdd")}
                        </button>
                      </div>
                      <TagPill
                        items={form.repository_filter}
                        onRemove={(i) => set("repository_filter", form.repository_filter.filter((_, idx) => idx !== i))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        {t("credentialScanning.gitFieldBranchFilter")} <span className="text-muted fw-normal">{t("credentialScanning.gitEmptyDefaultBranch")}</span>
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          value={branchFilterInput}
                          onChange={(e) => setBranchFilterInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBranchFilter(); } }}
                          placeholder={t("credentialScanning.gitPlaceholderBranchName")}
                        />
                        <button
                          type="button"
                          onClick={addBranchFilter}
                          style={{
                            padding: "7px 16px", background: "#5177ff", color: "#fff",
                            border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap",
                          }}
                        >
                          {t("credentialScanning.gitBtnAdd")}
                        </button>
                      </div>
                      <TagPill
                        items={form.branch_filter}
                        onRemove={(i) => set("branch_filter", form.branch_filter.filter((_, idx) => idx !== i))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.gitFieldCommitHistory")}</Form.Label>
                      <Form.Check
                        type="checkbox"
                        label={t("credentialScanning.gitToggleScanCommitHistory")}
                        checked={form.scan_commit_history}
                        onChange={(e) => set("scan_commit_history", e.target.checked)}
                        className="mb-2"
                      />
                      {form.scan_commit_history && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted small">{t("credentialScanning.gitDepthCommits")}</span>
                          <Form.Control
                            type="number"
                            min={1}
                            max={500}
                            value={form.commit_history_depth}
                            onChange={(e) => set("commit_history_depth", parseInt(e.target.value))}
                            style={{ width: 100 }}
                          />
                        </div>
                      )}
                    </Form.Group>

                    {/* Info box */}
                    <div className="cred-scan-token-info" style={{ borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
                      <strong>{t("credentialScanning.gitTokenStorage")}</strong>
                      <p className="mb-0 mt-1" style={{ fontSize: 12 }}>
                        {t("credentialScanning.gitTokenStorageNote")}
                        {isEdit && ` ${t("credentialScanning.gitTokenLeaveBlank")}`}
                      </p>
                    </div>
                  </Col>
                </Row>

                {errors.detail && (
                  <div className="mt-3 p-3 rounded" style={{ background: "#ffebee", color: "#c62828", fontSize: 13 }}>
                    {errors.detail}
                  </div>
                )}

                <Row className="mt-4">
                  <Col>
                    <Stack direction="horizontal" gap={3}>
                      <GeneralButton
                        onClickEvent={() => navigate("/dashboard/discovery/hardcoded-credentials/git-repos")}
                        variant="outlined"
                        value={t("credentialScanning.btnCancel")}
                        size="large"
                      />
                      <GeneralButton
                        type="submit"
                        onClickEvent={handleSubmit}
                        variant="contained"
                        value={saving ? t("credentialScanning.btnSavingEllipsis") : isEdit ? t("credentialScanning.gitBtnUpdateConnection") : t("credentialScanning.gitBtnAddConnection")}
                        size="large"
                        disabled={saving}
                      />
                    </Stack>
                  </Col>
                </Row>
              </Container>
            </Form>
          </div>
        </div>
      </Fade>
    </Slide>
  );
}

export default GitConnectionFormPage;
